import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit, PLATFORM_ID, Renderer2 } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LocalStorageService } from '@app/core/services/local-storage.service';
import { RawFileStoreService } from '@app/core/services/raw-file-store.service';
import { SEOService } from '@app/core/services/seo.service';
import * as fromRoot from '@app/reducers';
import { FileDropEntry } from '@app/shared/components/smash-file-drop/file-drop-entry';
import { FileSystemMetadata } from '@app/shared/components/smash-file-drop/interfaces';
import { getPathWithoutLastFragment } from '@app/shared/helpers/location';
import { Identity } from '@app/shared/models/identity';
import { DeliveryType, EmailDelivery, LinkDelivery } from '@app/shared/models/transferConfiguration';
import { DialogService } from '@ngneat/dialog';
import { Store } from '@ngrx/store';
import { GetRecordOutput } from '@smash-sdk/domain/ejs/01-2024';
import { Observable, Subscription } from 'rxjs';
import { filter, first, withLatestFrom } from 'rxjs/operators';
import { RawFileError } from '@app/client/uploader/interfaces/file';
import { LayoutActions, PrepareActions } from '@app/client/uploader/store/actions';
import * as fromUploader from '@app/client/uploader/store/reducers';
import { ModalFatalUploadErrorComponent } from '../send/modals/modal-fatal-upload-error/modal-fatal-upload-error.component';
import { ModalForbiddenEmailComponent } from './modals/modal-forbidden-email/modal-forbidden-email.component';
import { ModalForbiddenSubscriptionComponent } from './modals/modal-forbidden-subscription/modal-forbidden-subscription.component';
import { ModalPasswordRequiredComponent } from './modals/modal-password-required/modal-password-required.component';
import { ScrollPrepareService } from '@app/core/services/scroll-prepare.service';
import { UploaderError, UploaderStatus } from '@smash-sdk/uploader';
import { CloudfrontError } from '@app/shared/helpers/cloudfront';
import { ErrorDetails } from '@app/shared/helpers/uploader';

@Component({
  selector: 'app-prepare',
  templateUrl: './prepare.component.html',
  styleUrls: ['./prepare.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PrepareComponent implements OnInit, OnDestroy {

  dropbox$: Observable<any>;
  identity$: Observable<Identity>;
  rawFiles$: Observable<FileSystemMetadata[]>;
  totalSizeToUpload$: Observable<number>;
  uploadFormMode$: Observable<string>;
  domain$: Observable<GetRecordOutput['record']>;
  canConfigSmash$: Observable<boolean>;
  canConfigDropboxTransfer$: Observable<boolean>;
  transferSettings$: Observable<any>;
  rawFilesError$: Observable<RawFileError>;
  uploadError$: Observable<any>;
  subscriptions: Subscription[] = [];
  uploadStatus$: Observable<UploaderStatus>;

  lastScrollPosition = 0;
  constructor(
    private store$: Store<fromRoot.State & fromUploader.State>,
    private router: Router,
    private rawFileStoreService: RawFileStoreService,
    private dialog: DialogService,
    private route: ActivatedRoute,
    private seoService: SEOService,
    private scrollPrepare: ScrollPrepareService,
    private readonly localStorageService: LocalStorageService,
    @Inject(PLATFORM_ID) private platformId: any,
  ) {

    if (isPlatformBrowser(this.platformId)) {
      this.store$.dispatch(PrepareActions.resetDrawer());
      this.dropbox$ = this.store$.select(fromUploader.getDropboxPreview);
      this.rawFiles$ = this.store$.select(fromUploader.getAllRawFiles);
      this.identity$ = this.store$.select(fromRoot.getActiveIdentity);
      this.domain$ = this.store$.select(fromRoot.getCurrentDomain);
      this.uploadFormMode$ = this.store$.select(fromUploader.getUploadFormMode);
      this.totalSizeToUpload$ = this.store$.select(fromUploader.getTransferSize);
      this.uploadError$ = this.store$.select(fromUploader.getUploadError);
      this.rawFilesError$ = this.store$.select(fromUploader.getRawFilesError);
      this.uploadStatus$ = this.store$.select(fromUploader.getUploadStatus);
      if (this.isDropboxId) { // TO DO find a better way
        this.canConfigSmash$ = this.store$.select(fromUploader.getCanConfigureDropboxTransfer)
        this.transferSettings$ = this.store$.select(fromRoot.getDropboxTransferSettings);
      } else {
        this.canConfigSmash$ = this.store$.select(fromRoot.canConfigSmash);
        this.transferSettings$ = this.store$.select(fromRoot.getDomainTransferSettings);
      }


      this.subscriptions.push(
        this.rawFiles$.subscribe(files => {
          if (!files.length) {
            this.router.navigate([getPathWithoutLastFragment()], { relativeTo: this.route });
          }
        }),
        this.uploadStatus$.subscribe(uploadStatus => {
          if (uploadStatus === UploaderStatus.Started) {
            this.router.navigate(['../send'], { relativeTo: this.route });
          }
        }),
        this.uploadError$.pipe(filter(error => error !== null)).subscribe((error) => this.handleUploadError(error)),
      );
    }
  }

  get isDropboxId() { // TO DO find a better way
    return !!(this.route.snapshot.paramMap.get('dropboxId'));
  }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.loadDeliveryParameters();
      this.loadDeliveryParametersFromQueryString();
      this.seoService.setLandingTitle();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.map(subscription => subscription.unsubscribe());
    this.scrollPrepare.scrollDirection.next('down');
  }

  fileOver() {
    this.store$.dispatch(LayoutActions.UpdateFileOver({ fileOver: true }));
  }

  fileLeave() {
    this.store$.dispatch(LayoutActions.UpdateFileOver({ fileOver: false }));
  }

  async dropped(files: FileDropEntry[]) {
    // tslint:disable-next-line:no-shadowed-variable
    const identity = await this.identity$.pipe(first(identity => !!identity)).toPromise();
    await this.rawFileStoreService.setFiles(files, identity);
  }

  async openModalForbiddenEmail({ requestId }: { requestId: string }): Promise<void> {
    // tslint:disable-next-line:no-shadowed-variable
    const domain = await this.domain$.pipe(first(domain => !!domain)).toPromise();
    this.dialog.open(ModalForbiddenEmailComponent,
      {
        closeButton: false,
        enableClose: true,
        size: 'xs',
        data: {
          domain,
          requestId,
        },
      }
    );
  }

  openModalForbiddenSubscription({ requestId }: { requestId: string }): void {
    this.dialog.open(ModalForbiddenSubscriptionComponent,
      {
        closeButton: false,
        enableClose: true,
        size: 'sm',
        data: {
          requestId,
        },
      });
  }

  openModalPasswordRequired({ requestId }: { requestId: string }): void {
    this.dialog.open(ModalPasswordRequiredComponent,
      {
        closeButton: false,
        enableClose: true,
        size: 'xs',
        data: {
          requestId,
        },
      });
  }

  onFileOver(over) {
    if (over) {
      this.store$.dispatch(LayoutActions.UpdateFileOver({ fileOver: true }));
    } else {
      this.store$.dispatch(LayoutActions.UpdateFileOver({ fileOver: false }));
    }
  }

  loadDeliveryParameters() {
    this.identity$.pipe(
      first(),
      withLatestFrom(this.domain$),
    ).subscribe(([identity, domain]) => {
      const lastDeliveryParameters = this.getLastDeliveryParameters();

      if (this.isDropboxId) {
        // If upload on dropbox force the uploadMode to Email to handle subject && description
        this.store$.dispatch(PrepareActions.setUploadFormMode({ uploadFormMode: "Email" }));
      } else if (lastDeliveryParameters?.type && typeof lastDeliveryParameters?.type === 'string') {
        // Set the upload form mode based on the last delivery parameters or the default value if the last delivery parameters are not valid anymore.
        // Example : if the user has previously selected a delivery type that is not available anymore.
        const options = domain?.options?.transfer?.delivery?.options as DeliveryType[];
        // FIX ME It should be the default value from the domain but it is not the correct one for now.
        // Example : admin remove the email delivery option from the domain. The default value should be the link delivery. But it is not the case.
        const defaultUploadFormMode = domain?.options?.transfer?.delivery?.options[0] as DeliveryType;
        const uploadFormMode = options?.includes(lastDeliveryParameters?.type) ? lastDeliveryParameters?.type : defaultUploadFormMode;
        this.store$.dispatch(PrepareActions.setUploadFormMode({ uploadFormMode }));
      }

      if (identity?.username) {
        this.store$.dispatch(PrepareActions.SetTransferSender({ sender: { email: identity?.username } }));
      } else if (lastDeliveryParameters?.sender) {
        this.store$.dispatch(PrepareActions.SetTransferSender({ sender: lastDeliveryParameters?.sender }));
      }
    });
  }

  getLastDeliveryParameters(): EmailDelivery | LinkDelivery | undefined {
    const lastDeliveryParametersString: string = this.localStorageService.getItem('lastTransferParameters');
    if (lastDeliveryParametersString) {
      return JSON.parse(decodeURIComponent(escape(atob(lastDeliveryParametersString)))) as EmailDelivery | LinkDelivery;
    } else {
      return undefined;
    }
  }

  loadDeliveryParametersFromQueryString() {
    this.identity$.pipe(
      first(),
      withLatestFrom(this.domain$),
    ).subscribe(([identity, domain]) => {
      const prefilledTransferParametersString: string = this.localStorageService.getItem('prefilledTransferParameters');
      if (prefilledTransferParametersString) {
        const params = JSON.parse(prefilledTransferParametersString);
        if (params) {
          const { sender, name, receivers, uploadFormMode, description, title } = params;
          const options = domain?.options?.transfer?.delivery?.options as DeliveryType[] || [];
          if (uploadFormMode && options.includes(uploadFormMode as DeliveryType)) {

            this.store$.dispatch(PrepareActions.setUploadFormMode({ uploadFormMode }));
          }
          if (sender && !identity?.username) {
            this.store$.dispatch(PrepareActions.SetTransferSender({ sender: { email: sender, name } }));
          }
          if (description) {
            this.store$.dispatch(PrepareActions.SetEmailDescription({ description }));
          }
          if (title) {
            this.store$.dispatch(PrepareActions.SetEmailTitle({ title }));
            this.store$.dispatch(PrepareActions.SetLinkTitle({ title }));
          }
          if (receivers) {
            this.store$.dispatch(PrepareActions.SetEmailReceivers({ receivers }));
          }
        }
      }
    });
  }

  openModalUploadFatalError({ errorName, errorMessage, errorId, errorDetails }: { errorName?: string, errorMessage?: string, errorId?: string; errorDetails?: ErrorDetails }): void {
    this.dialog.open(ModalFatalUploadErrorComponent,
      {
        closeButton: false,
        enableClose: false,
        size: 'xs',
        data: {
          errorName,
          errorMessage,
          errorId,
          errorDetails,
        },
      });
  }

  // FIX ME we need to use the errorName class attribute from uploader errors constructor when it is implemented
  private handleUploadError(error: CloudfrontError<UploaderError>) {
    if (error?.name === 'EmailNotAllowedError') {
      this.openModalForbiddenEmail({ requestId: error?.requestId });
    } else if (error?.name === 'InvalidSubscriptionError') {
      this.openModalForbiddenSubscription({ requestId: error?.requestId });
    } else if (error?.name === 'PasswordRequiredError') {
      this.openModalPasswordRequired({ requestId: error?.requestId });
    } else if (error?.name === 'TransferAlreadyQueuedError') {
      this.store$.dispatch(PrepareActions.DisplayModalSizePremium());
    } else if (error?.name === 'DropboxDisabledError') {
      this.openModalUploadFatalError({
        errorName: error?.name,
        errorMessage: error?.message,
        errorId: error?.errorId,
        errorDetails: error?.details,
      });
    }
  }

  onSmallDeviceScroll(event) {
    if (event.target.scrollTop) {
      this.scrollPrepare.scrollDirection.next('up');
    } else {
      this.scrollPrepare.scrollDirection.next('down');
    }
  }
}
